import { forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react';
import TemplateBoxTwoFields from '../../form-templates/TemplateBoxTwoFields';
import {
  getSystemInfoFieldsType,
  SystemInfoFieldsTypes,
  SystemInfoInterface
} from '../../../utils/types/systemInfo';
import { SystemInfoTemplateItem } from '../../../utils/types/systemInfoTemplate';
import { Alert, Button, Grid } from '@mui/material';
import { systemInfoTemplateItemInitialState } from '../../../context/reducers/systemInfoTemplateReducer';
import { systemInfoTemplateFieldsSchema } from '../../form-templates/system-info/SystemInfoTemplateSchema';
import { validateFormArray } from '../../../utils/form';
import { useQueryParams } from '../../../hooks/useQueryParams';
import { SystemInfoContext } from '../../../context/systemInfoContext';
import { VesselContext } from '../../../context/vesselContext';
import { VesselInterface } from '../../../utils/types/vessel';
import { SystemInfoTemplateInterface } from '../../../utils/types/systemInfoTemplate';
import SystemInfoTemplateService from '../../../services/systemInfoTemplate';
import { cloneTemplateFieldsForVessel } from './systemInfoUtils';

const initialErrorValues = {
  name: '',
  fields: [{ label: '', field: '' }]
};

type ValidationErrors = {
  name: string;
  fields: { label: string; field: string }[];
};

type AlertType = {
  severity: 'success' | 'info' | 'warning' | 'error';
  text: string;
};

export type SystemInfoFormHandle = {
  handleSave: () => void;
  applyTemplate: (template: SystemInfoTemplateInterface) => Promise<void>;
};

export const SystemInfoForm = forwardRef<SystemInfoFormHandle>((_props, ref) => {
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(initialErrorValues);
  const [template, setTemplate] = useState<SystemInfoTemplateItem[]>([]);
  const [noFieldsError, setNoFieldsError] = useState(false);
  const { createSystemInfo, updateSystemInfo, listSystemInfo } = useContext(SystemInfoContext);
  const [alert, setAlert] = useState<AlertType | undefined>(undefined);
  const [systemInfo, setSystemInfo] = useState<SystemInfoInterface | null>(null);
  const [appliedTemplateId, setAppliedTemplateId] = useState<string | undefined>(undefined);
  const { viewVessel, state: vesselState } = useContext(VesselContext);
  const [vessel, setVessel] = useState<VesselInterface | undefined>(undefined);

  const query = useQueryParams();
  const vesselId = query.get('vesselId');

  const loadSystemInfo = async () => {
    if (!vesselId) return;

    const data = await listSystemInfo(vesselId);
    const record = data?.results?.[0] ?? null;
    setSystemInfo(record);
    setTemplate(JSON.parse(record?.template || '[]'));
    setAppliedTemplateId(record?.system_info_template_id);
  };

  useEffect(() => {
    loadSystemInfo();
  }, [vesselId]);

  useEffect(() => {
    if (vesselId) viewVessel(vesselId, { join: 'owner' });
  }, [vesselId]);

  useEffect(() => {
    if (vesselState.view.data) setVessel(vesselState.view.data);
  }, [vesselState.view]);

  const applyTemplate = async (selectedTemplate: SystemInfoTemplateInterface) => {
    let fullTemplate = selectedTemplate;

    if (!selectedTemplate.template) {
      const service = new SystemInfoTemplateService();
      fullTemplate = await service.view(selectedTemplate.id);
    }

    const fields = JSON.parse(fullTemplate.template || '[]');
    setTemplate(cloneTemplateFieldsForVessel(fields));
    setAppliedTemplateId(fullTemplate.id);
    setNoFieldsError(false);
    setAlert({
      severity: 'info',
      text: `Template "${fullTemplate.name}" loaded. Save to apply it to this vessel.`
    });
  };

  const removeField = (index: number) => {
    setTemplate((prev) => prev.filter((_item, i) => i !== index));
  };

  const handleSave = async () => {
    if (template.length === 0) {
      setNoFieldsError(true);
      return;
    }

    const { errorSummary: fieldsErrors } = validateFormArray(
      systemInfoTemplateFieldsSchema,
      template.map((field) => ({
        label: field.label,
        field: field.field.type
      }))
    );

    setValidationErrors({
      name: validationErrors.name,
      fields: fieldsErrors
    });

    if (fieldsErrors.length > 0 || !vesselId) return;

    const payload = {
      name: vessel?.name || '',
      template: JSON.stringify(template),
      vessel_id: vesselId,
      ...(appliedTemplateId ? { system_info_template_id: appliedTemplateId } : {})
    };

    if (systemInfo?.id) {
      await updateSystemInfo(systemInfo.id, payload);
    } else {
      const created = await createSystemInfo(payload);
      if (created) {
        setSystemInfo(created);
      } else {
        await loadSystemInfo();
      }
    }

    setAlert({
      severity: 'success',
      text: 'System info saved successfully'
    });
  };

  useImperativeHandle(ref, () => ({
    handleSave,
    applyTemplate
  }));

  const onChangeValues = (type: string, value: SystemInfoFieldsTypes, index: number) => {
    setTemplate((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              ...(type === 'name' ? { label: value } : { field: { ...item.field, type: value } })
            }
          : item
      )
    );
  };

  const addField = () => {
    setTemplate((prev) => [...prev, systemInfoTemplateItemInitialState]);
    setNoFieldsError(false);
  };

  const renderFields = template.map((item, i) => {
    const validationErrorField = validationErrors.fields[i] || { label: '', field: '' };
    return (
      <div key={i}>
        <TemplateBoxTwoFields
          showDeleteIcon={i !== 0}
          item={{
            name: item.label || '',
            type: item.field?.type || ''
          }}
          onDelete={() => removeField(i)}
          boxValues={getSystemInfoFieldsType()}
          validationErrors={{
            name: validationErrorField.label,
            type: validationErrorField.field
          }}
          onChangeFieldName={(e) => onChangeValues('name', e as SystemInfoFieldsTypes, i)}
          handleDropdownChange={(e) => onChangeValues('type', e as SystemInfoFieldsTypes, i)}
        />

        <div style={dividerStyle} />
      </div>
    );
  });

  return (
    <>
      {alert && (
        <Grid container sx={{ marginBottom: '11px' }}>
          <Grid item xs={12}>
            <Alert severity={alert.severity} onClose={() => setAlert(undefined)}>
              {alert.text}
            </Alert>
          </Grid>
        </Grid>
      )}
      {template.length === 0 && (
        <Grid item xs={12} sx={{ marginBottom: 2, color: '#85878F' }}>
          No system info configured yet. Load a template to get started.
        </Grid>
      )}
      {renderFields}
      {noFieldsError && (
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <p style={{ color: 'red' }}>Please add at least one field.</p>
        </Grid>
      )}
      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
        <Button
          variant="outlined"
          disabled={false}
          type="button"
          className="actionButton"
          onClick={addField}>
          Add field
        </Button>
      </Grid>
    </>
  );
});

export default SystemInfoForm;

const dividerStyle = { marginBottom: '24px', width: '100%' };
